import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { hitApi, dateFormatter, commonToast } from "../../../components/commonAction";
import BillingTabs from "./BillingTabs";

const STATUS_PILL = {
    completed: "pxl-pill--success",
    pending:   "pxl-pill--warning",
    failed:    "pxl-pill--danger",
    refunded:  "pxl-pill--muted",
};

export default function Transactions({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(true);

    // filters
    const [status, setStatus] = useState("");
    const [type, setType]     = useState("");
    const [q, setQ]           = useState("");
    const [from, setFrom]     = useState("");
    const [to, setTo]         = useState("");

    const load = (page = 1) => {
        setLoading(true);
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (type)   params.set("type", type);
        if (q)      params.set("q", q);
        if (from)   params.set("from", from);
        if (to)     params.set("to", to);
        params.set("page", page);
        params.set("limit", 25);
        hitApi({
            url: `admin/billing/transactions?${params.toString()}`,
            method: "GET", data: {}, alert: false,
        }, (resp) => {
            setLoading(false);
            if (resp?.status) {
                setRows(resp.data?.transactions || []);
                setPagination(resp.data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 });
            }
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(1); }, []);

    const onApply = (e) => { e.preventDefault(); load(1); };
    const onClear = () => {
        setStatus(""); setType(""); setQ(""); setFrom(""); setTo("");
        setTimeout(() => load(1), 0);
    };

    const [reconcilingId, setReconcilingId] = useState(null);
    const reconcile = (txn) => {
        setReconcilingId(txn._id);
        hitApi({
            url: `admin/billing/transactions/${txn._id}/reconcile`,
            method: "POST", data: {}, alert: false,
        }, (resp) => {
            setReconcilingId(null);
            const ppStatus = resp?.data?.paypalStatus;
            if (resp?.status) {
                commonToast(
                    resp.data?.transaction?.status === "completed" ? "success" : "error",
                    resp.message + (ppStatus ? ` (PayPal: ${ppStatus})` : ""),
                );
                load(pagination.page);
            } else {
                commonToast("error", resp?.message || "Reconcile failed.");
            }
        });
    };

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap"><div className="pxl-headingTop"><h2 className="pxl-rb-header-heading">Billing</h2></div></div>
            <BillingTabs />

            <div className="pxl-panel mb-3">
                <div className="pxl-panel-body pxl-panel-body--compact">
                    <form onSubmit={onApply}>
                        <div className="row g-2">
                            <div className="col-lg-2 col-md-3 col-sm-6">
                                <div className="pxl_input_feilds pxl-input-compact" style={{ marginBottom: 0 }}>
                                    <label>Status</label>
                                    <select value={status} onChange={e => setStatus(e.target.value)}>
                                        <option value="">Any</option>
                                        <option value="completed">Completed</option>
                                        <option value="pending">Pending</option>
                                        <option value="failed">Failed</option>
                                        <option value="refunded">Refunded</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6">
                                <div className="pxl_input_feilds pxl-input-compact" style={{ marginBottom: 0 }}>
                                    <label>Type</label>
                                    <select value={type} onChange={e => setType(e.target.value)}>
                                        <option value="">Any</option>
                                        <option value="tier">Tier</option>
                                        <option value="plan">Plan</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-lg-4 col-md-6 col-sm-12">
                                <div className="pxl_input_feilds pxl-input-compact" style={{ marginBottom: 0 }}>
                                    <label>Search</label>
                                    <input
                                        value={q}
                                        onChange={e => setQ(e.target.value)}
                                        placeholder="email or paypal id"
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6 col-sm-6">
                                <div className="pxl_input_feilds pxl-input-compact" style={{ marginBottom: 0 }}>
                                    <label>From</label>
                                    <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6 col-sm-6">
                                <div className="pxl_input_feilds pxl-input-compact" style={{ marginBottom: 0 }}>
                                    <label>To</label>
                                    <input type="date" value={to} onChange={e => setTo(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button type="button" className="btn btn-light" onClick={onClear}>Clear</button>
                            <button type="submit" className="pxlCommon-btn">Apply filters</button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="pxl-panel">
                <div className="pxl-panel-header">
                    <span>Transactions ({pagination.total})</span>
                    <button className="btn btn-sm btn-light" onClick={() => load(pagination.page)}>Refresh</button>
                </div>
                <div className="pxl-panel-body pxl-panel-body--flush">
                    {!loading && rows.length === 0 ? (
                        <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No transactions match the current filters.</div>
                    ) : (
                        <div className="pxl_table table-responsive">
                            <table className="pxl_table">
                                <thead>
                                    <tr>
                                        <th>Created</th>
                                        <th>User</th>
                                        <th>Type</th>
                                        <th>Detail</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Affiliate</th>
                                        <th>PayPal</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <>
                                            {[0, 1, 2, 3, 4].map(i => (
                                                <tr key={i}>
                                                    <td><span className="pxl-sk" style={{ width: '70%', height: 13, display: 'block' }} /></td>
                                                    <td>
                                                        <span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} />
                                                        <span className="pxl-sk" style={{ width: '45%', height: 10, display: 'block', marginTop: 4 }} />
                                                    </td>
                                                    <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: '65%', height: 13, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: '45%', height: 13, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                                    <td><span className="pxl-sk" style={{ width: 28, height: 28, borderRadius: 6, display: 'block' }} /></td>
                                                </tr>
                                            ))}
                                        </>
                                    ) : rows.map(r => (
                                        <tr key={r._id}>
                                            <td><p style={{ color: "#8a8fa6", fontSize: 12 }}>{dateFormatter(r.createdAt)}</p></td>
                                            <td>
                                                {r.user ? (
                                                    <>
                                                        <p style={{ margin: 0 }}>{r.user.name || "(unknown)"}</p>
                                                        <p style={{ color: "#8a8fa6", fontSize: 12, margin: 0 }}>{r.user.email}</p>
                                                    </>
                                                ) : <span style={{ color: "#8a8fa6" }}>—</span>}
                                            </td>
                                            <td>
                                                <span className="pxl-pill pxl-pill--muted">{r.type}</span>
                                            </td>
                                            <td>
                                                {r.tier && (
                                                    <p style={{ margin: 0, fontSize: 13 }}>
                                                        <span style={{ color: "#8a8fa6", fontSize: 11 }}>tier</span>{" "}
                                                        <code>{r.tier.code}</code> — {r.tier.name}
                                                    </p>
                                                )}
                                                {r.product && r.plan && (
                                                    <p style={{ margin: 0, fontSize: 13 }}>
                                                        <span style={{ color: "#8a8fa6", fontSize: 11 }}>{r.product.name}</span>{" "}
                                                        / <code>{r.plan.code}</code>
                                                    </p>
                                                )}
                                                {r.refundedAt && (
                                                    <p style={{ color: "#8a8fa6", fontSize: 11, margin: 0 }}>
                                                        refunded {dateFormatter(r.refundedAt)}{r.refundReason ? ` — ${r.refundReason}` : ""}
                                                    </p>
                                                )}
                                            </td>
                                            <td>
                                                <p><strong>{r.currency} {Number(r.amount || 0).toFixed(2)}</strong></p>
                                            </td>
                                            <td>
                                                <span className={`pxl-pill ${STATUS_PILL[r.status] || "pxl-pill--muted"}`}>{r.status}</span>
                                            </td>
                                            <td>
                                                {r.affiliate ? (
                                                    <Link to={`/admin/affiliates/${r.affiliate._id}`}>
                                                        <code>{r.affiliate.referralCode}</code>
                                                    </Link>
                                                ) : <span style={{ color: "#8a8fa6", fontSize: 12 }}>direct</span>}
                                            </td>
                                            <td style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11 }}>
                                                {r.paypalSubscriptionId && <p style={{ margin: 0 }} title="Subscription">{r.paypalSubscriptionId}</p>}
                                                {r.paypalOrderId && <p style={{ margin: 0 }} title="Order">{r.paypalOrderId}</p>}
                                                {r.paypalCaptureId && <p style={{ color: "#8a8fa6", margin: 0 }} title="Capture">{r.paypalCaptureId}</p>}
                                                {!r.paypalSubscriptionId && !r.paypalOrderId && !r.paypalCaptureId && (
                                                    <span style={{ color: "#8a8fa6" }}>—</span>
                                                )}
                                            </td>
                                            <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                                {r.status === "pending" && (r.paypalSubscriptionId || r.paypalOrderId) && (
                                                    <button
                                                        className="btn btn-sm btn-link"
                                                        disabled={reconcilingId === r._id}
                                                        onClick={() => reconcile(r)}
                                                        title="Pull current status from PayPal and apply it locally — useful when the webhook didn't fire"
                                                    >
                                                        {reconcilingId === r._id ? "Syncing…" : "Reconcile"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {pagination.totalPages > 1 && (
                    <div className="pxl-panel-header" style={{ borderBottom: "none", borderTop: "1px solid #e3e7f0" }}>
                        <span style={{ color: "#8a8fa6", fontSize: 12, fontWeight: 400 }}>
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-light"
                                disabled={pagination.page <= 1 || loading}
                                onClick={() => load(pagination.page - 1)}
                            >Previous</button>
                            <button
                                className="btn btn-sm btn-light"
                                disabled={pagination.page >= pagination.totalPages || loading}
                                onClick={() => load(pagination.page + 1)}
                            >Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
